import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cellRefMatches } from './types';
import type { ReviewStatus, ReviewStore } from './types';

/** Return a copy of `set` with `value` toggled in: removed if present, else added. */
const withToggled = (set: Set<string>, value: string): Set<string> => {
  const next = new Set(set);
  if (!next.delete(value)) {
    next.add(value);
  }
  return next;
};

const useReviewStore = create<ReviewStore>()(
  devtools(
    set => ({
      reviewItems: [],
      selectedCardIds: new Set<string>(),
      filterSecurities: new Set<string>(),
      filterMetrics: new Set<string>(),
      filterStatus: 'all',
      filterProvenance: 'ai',
      hoveredReviewCell: null,
      selectedReviewCell: null,
      drawerOpen: false,
      paneCollapsed: false,
      showAiColumnControls: false,

      setReviewItems: items => set(() => ({ reviewItems: items }), false, 'setReviewItems'),

      toggleCardSelection: id =>
        set(state => ({ selectedCardIds: withToggled(state.selectedCardIds, id) }), false, 'toggleCardSelection'),

      selectCards: ids => set(() => ({ selectedCardIds: new Set(ids) }), false, 'selectCards'),

      clearSelection: () => set(() => ({ selectedCardIds: new Set<string>() }), false, 'clearSelection'),

      toggleFilterSecurity: label =>
        set(state => ({ filterSecurities: withToggled(state.filterSecurities, label) }), false, 'toggleFilterSecurity'),

      toggleFilterMetric: label =>
        set(state => ({ filterMetrics: withToggled(state.filterMetrics, label) }), false, 'toggleFilterMetric'),

      clearReviewFilter: () =>
        set(() => ({ filterSecurities: new Set<string>(), filterMetrics: new Set<string>() }), false, 'clearReviewFilter'),

      setFilterStatus: status => set(() => ({ filterStatus: status }), false, 'setFilterStatus'),

      setFilterProvenance: provenance =>
        set(() => ({ filterProvenance: provenance }), false, 'setFilterProvenance'),

      removeReviewItem: id =>
        set(
          state => {
            const next = new Set(state.selectedCardIds);
            next.delete(id);
            return {
              reviewItems: state.reviewItems.filter(i => i.id !== id),
              selectedCardIds: next,
            };
          },
          false,
          'removeReviewItem'
        ),

      setReviewItemStatus: (id, status) =>
        set(
          state => {
            const next = new Set(state.selectedCardIds);
            next.delete(id);
            // Settling an item also drops any grid outline pointing at its cell
            // (hovered/selected review cell), so a dismissed/accepted cell never
            // keeps the purple review ring (SCAL-8976 follow-up). A card the user
            // re-activates from the Reviewed section re-selects explicitly.
            const item = status !== 'pending' ? state.reviewItems.find(i => i.id === id) : undefined;
            const ref = item ? { securityId: item.securityId, fieldAlias: item.fieldAlias } : null;
            return {
              reviewItems: state.reviewItems.map(i => (i.id === id ? { ...i, status } : i)),
              selectedCardIds: next,
              ...(ref && cellRefMatches(state.hoveredReviewCell, ref) ? { hoveredReviewCell: null } : {}),
              ...(ref && cellRefMatches(state.selectedReviewCell, ref) ? { selectedReviewCell: null } : {}),
            };
          },
          false,
          'setReviewItemStatus'
        ),

      setHoveredReviewCell: cell => set(() => ({ hoveredReviewCell: cell }), false, 'setHoveredReviewCell'),

      setSelectedReviewCell: cell => set(() => ({ selectedReviewCell: cell }), false, 'setSelectedReviewCell'),

      setDrawerOpen: open => set(() => ({ drawerOpen: open }), false, 'setDrawerOpen'),

      setPaneCollapsed: collapsed => set(() => ({ paneCollapsed: collapsed }), false, 'setPaneCollapsed'),

      setShowAiColumnControls: show =>
        set(() => ({ showAiColumnControls: show }), false, 'setShowAiColumnControls'),
    }),
    { name: 'ReviewStore' }
  )
);

/**
 * Subscribe to whether one specific cell should reveal its AI merge text / outline:
 * true when (securityId, fieldAlias) matches the hovered OR the selected review cell.
 * The selector returns a plain boolean, so a cell's viewer only re-renders when its
 * own match flips - hovering a card does not re-render the whole grid.
 */
export const useCellShowsMerge = (securityId?: number, fieldAlias?: string): boolean =>
  useReviewStore(s => {
    if (securityId == null || !fieldAlias) {
      return false;
    }
    const target = { securityId, fieldAlias };
    return cellRefMatches(s.hoveredReviewCell, target) || cellRefMatches(s.selectedReviewCell, target);
  });

/**
 * Live review status of one cell's item, or undefined when the store holds no
 * item for it (drawer closed / not an evidenced cell). This is the SINGLE source
 * of truth the grid viewers share with the panel: the panel flips an item's
 * status on accept/decline, and a subscribed cell viewer re-renders immediately
 * (metadata mutations alone never repaint - the cell event listener bails out
 * when the value is unchanged), so the grid and the panel can never disagree.
 */
export const useReviewItemStatus = (securityId?: number, fieldAlias?: string): ReviewStatus | undefined =>
  useReviewStore(s =>
    securityId == null || !fieldAlias
      ? undefined
      : s.reviewItems.find(i => i.id === `${securityId}:${fieldAlias}`)?.status
  );

/**
 * Number of items still PENDING review. The aggregator now emits accepted and
 * dismissed items too (so reviewed cards render at the bottom), so the "needs
 * review" count must filter to pending - not the full list length.
 */
export const usePendingReviewCount = (): number =>
  useReviewStore(s => s.reviewItems.reduce((n, i) => (i.status === 'pending' ? n + 1 : n), 0));

/**
 * Whether the review drawer is open. Pending AI cells subscribe to this so they paint
 * AI-purple by default while the drawer is open and revert when it closes (SCAL-9150).
 * Returns a plain boolean, so a cell's viewer re-renders only when the flag flips.
 */
export const useReviewDrawerOpen = (): boolean => useReviewStore(s => s.drawerOpen);

/**
 * Whether the user collapsed the review side panel via the standalone
 * pending-review badge (SCAL-9181). The cap-table drawer withholds its
 * sidePanel while this is true.
 */
export const useReviewPaneCollapsed = (): boolean => useReviewStore(s => s.paneCollapsed);

export default useReviewStore;
